import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useToast } from "../hooks/useToast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import {
  fetchStores,
  fetchStoreStats,
  fetchStoreGroups,
  createStore,
  updateStore,
  deleteStore,
  createStoreGroup,
} from "../store/actions/storeActions";
import { fetchPlan } from "../store/actions/billingActions";
import { usePlanLimits } from "../hooks/usePlanLimits";
import * as storeServices from "../services/storeServices";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/Table";
import {
  Store,
  ShoppingCart,
  Users,
  BarChart2,
  Plus,
  Settings,
  Download,
  Search,
  Filter,
  MoreVertical,
  RefreshCw,
  Edit,
  Trash2,
  Package,
  Percent,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const StatCard = styled(Card)`
  padding: 20px 20px 18px;
`;

const StatTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 26px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const IconBubble = styled.div`
  border-radius: 9999px;
  padding: 10px;
  background: rgba(129, 140, 248, 0.1);
  color: #4f46e5;
`;

const statIcons = [
  { nameKey: "stores.totalStores", key: "totalStores", change: "+1", icon: Store },
  { nameKey: "stores.activeOrders", key: "activeOrders", change: "+12%", icon: ShoppingCart },
  { nameKey: "stores.totalCustomers", key: "totalCustomers", change: "+18%", icon: Users },
  { nameKey: "stores.totalRevenue", key: "totalRevenue", change: "+25%", icon: BarChart2 },
];

const chartData = [
  { name: "Jan", sales: 4000, orders: 240 },
  { name: "Feb", sales: 3000, orders: 198 },
  { name: "Mar", sales: 5000, orders: 305 },
  { name: "Apr", sales: 2780, orders: 189 },
  { name: "May", sales: 1890, orders: 142 },
  { name: "Jun", sales: 2390, orders: 178 },
];

const GroupsGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const GroupCard = styled(Card)`
  padding: 16px 18px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 24px;
`;

const ModalCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  padding: 24px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const ModalSub = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.body};
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.body};
  margin-bottom: 6px;
`;

const FormHint = styled.span`
  display: block;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

export default function Stores() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { stores = [], stats, groups = [], loading } = useSelector((state) => state.store);
  const { canAddStore, storeLimitReached, storeLimit, trialExpired } = usePlanLimits({ storeCount: stores.length });

  const syncTypeKeys = { products: "stores.syncTypeProducts", orders: "stores.syncTypeOrders", customers: "stores.syncTypeCustomers", coupons: "stores.syncTypeCoupons", all: "stores.syncTypeAll", inventory: "stores.syncTypeInventory" };

  const [connectOpen, setConnectOpen] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState("woocommerce");
  const [connectName, setConnectName] = useState("");
  const [connectUrl, setConnectUrl] = useState("");
  const [connectConsumerKey, setConnectConsumerKey] = useState("");
  const [connectConsumerSecret, setConnectConsumerSecret] = useState("");
  const [connectAccessToken, setConnectAccessToken] = useState("");
  const [connectError, setConnectError] = useState("");
  const [tab, setTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [menuStore, setMenuStore] = useState(null);
  const [editStore, setEditStore] = useState(null);
  const [deleteStoreId, setDeleteStoreId] = useState(null);
  const [syncingStoreId, setSyncingStoreId] = useState(null);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupStoreIds, setGroupStoreIds] = useState([]);
  const [groupSubmitting, setGroupSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchPlan());
    dispatch(fetchStores());
    dispatch(fetchStoreStats());
  }, [dispatch]);

  useEffect(() => {
    if (tab === "groups") dispatch(fetchStoreGroups());
  }, [dispatch, tab]);

  const statData = statIcons.map((s) => {
    const raw = stats?.[s.key] ?? 0;
    const value = s.key === "totalRevenue"
      ? `$${raw >= 1000 ? `${(raw / 1000).toFixed(1)}k` : raw.toLocaleString()}`
      : String(raw);
    return { ...s, value: stats != null ? value : "—" };
  });

  const filteredStores = stores.filter(
    (s) =>
      `${s.name || ""} ${s.url || ""}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColors = (status) => {
    const map = {
      online: { bg: "#dcfce7", fg: "#166534" },
      offline: { bg: "#fee2e2", fg: "#991b1b" },
      maintenance: { bg: "#fef9c3", fg: "#854d0e" },
    };
    return map[status] || { bg: "#f3f4f6", fg: "#111827" };
  };

  async function handleSync(store, type) {
    setMenuStore(null);
    setSyncingStoreId(store._id);
    try {
      if (type === "products") await storeServices.syncStoreProducts(store._id);
      else if (type === "orders") await storeServices.syncStoreOrders(store._id);
      else if (type === "customers") await storeServices.syncStoreCustomers(store._id);
      else if (type === "coupons") await storeServices.syncStoreCoupons(store._id);
      else if (type === "all") await storeServices.syncStoreAll(store._id);
      else if (type === "inventory") await storeServices.syncStoreInventory(store._id);
      dispatch(fetchStores());
      dispatch(fetchStoreStats());
      toast({ title: t("stores.syncComplete"), description: t("stores.syncCompleteDesc", { type: t(syncTypeKeys[type] || type) }) });
    } catch (err) {
      toast({ title: t("stores.syncFailed"), description: err.message || t("stores.syncFailed"), variant: "destructive" });
    } finally {
      setSyncingStoreId(null);
    }
  }

  function handleImportProducts(store) {
    setMenuStore(null);
    navigate("/get-started", { state: { openProductWizard: true, preselectedStoreId: store._id } });
  }

  async function handleEditStoreSubmit(e) {
    e.preventDefault();
    if (!editStore) return;
    const name = (e.target.name?.value || "").trim();
    const url = (e.target.url?.value || "").trim();
    const status = e.target.status?.value || "online";
    const location = (e.target.location?.value || "").trim();
    if (!name || !url) return;
    await dispatch(updateStore(editStore._id, { name, url, status, location }));
    dispatch(fetchStores());
    setEditStore(null);
    toast({ title: t("stores.storeUpdated"), description: t("stores.storeSaved") });
  }

  async function handleDeleteStoreConfirm() {
    if (!deleteStoreId) return;
    await dispatch(deleteStore(deleteStoreId));
    dispatch(fetchStores());
    dispatch(fetchStoreStats());
    setDeleteStoreId(null);
    toast({ title: t("stores.storeDeleted"), description: t("stores.storeRemoved") });
  }

  async function handleCreateGroupSubmit(e) {
    e.preventDefault();
    const name = (e.target.groupName?.value || "").trim();
    if (!name) return;
    setGroupSubmitting(true);
    const result = await dispatch(createStoreGroup({ name, storeIds: groupStoreIds }));
    setGroupSubmitting(false);
    if (result?.success) {
      setGroupModalOpen(false);
      setGroupName("");
      setGroupStoreIds([]);
      dispatch(fetchStoreGroups());
      toast({ title: t("stores.groupCreated"), description: t("stores.groupSaved") });
    } else {
      toast({ title: t("stores.failed"), description: result?.error || t("stores.couldNotCreateGroup"), variant: "destructive" });
    }
  }

  async function handleConnectSubmit(e) {
    e.preventDefault();
    setConnectError("");
    if (!connectName.trim() || !connectUrl.trim()) {
      setConnectError(t("stores.nameAndUrlRequired"));
      return;
    }
    const payload = {
      name: connectName.trim(),
      url: connectUrl.trim(),
      platform: connectPlatform,
    };
    if (connectPlatform === "woocommerce") {
      if (!connectConsumerKey.trim() || !connectConsumerSecret.trim()) {
        setConnectError(t("stores.consumerKeySecretRequired"));
        return;
      }
      payload.credentials = { consumerKey: connectConsumerKey.trim(), consumerSecret: connectConsumerSecret.trim() };
    } else {
      if (!connectAccessToken.trim()) {
        setConnectError(t("stores.accessTokenRequired"));
        return;
      }
      payload.credentials = { accessToken: connectAccessToken.trim() };
    }
    const result = await dispatch(createStore(payload));
    if (result && result.success) {
      setConnectOpen(false);
      setConnectName("");
      setConnectUrl("");
      setConnectConsumerKey("");
      setConnectConsumerSecret("");
      setConnectAccessToken("");
      setConnectError("");
      toast({ title: t("stores.storeConnected"), description: t("stores.storeAdded") });
    } else {
      const errMsg = (result && result.error) || t("stores.failedToConnectStore");
      setConnectError(errMsg);
      toast({ title: t("stores.connectionFailed"), description: errMsg, variant: "destructive" });
    }
  }

  return (
    <div className="animate-fadeIn">
      <div style={{ marginBottom: 24 }}>
        <Title>{t("stores.title")}</Title>
        <Sub>{t("stores.subtitle")}</Sub>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("stores.overview")}</TabsTrigger>
          <TabsTrigger value="stores">{t("stores.allStores")}</TabsTrigger>
          <TabsTrigger value="analytics">{t("stores.analytics")}</TabsTrigger>
          <TabsTrigger value="groups">{t("stores.storeGroups")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }} className="space-y-6">
            <StatsGrid>
              {statData.map((s) => {
                const Icon = s.icon;
                return (
                  <StatCard key={s.nameKey}>
                    <StatTop>
                      <div>
                        <StatName>{t(s.nameKey)}</StatName>
                        <StatValue>{loading ? "…" : s.value}</StatValue>
                      </div>
                      <IconBubble>
                        <Icon size={20} />
                      </IconBubble>
                    </StatTop>
                    <div style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>
                        {s.change}
                      </span>
                      <span style={{ fontSize: 13, color: "#6b7280" }}> {t("stores.fromLastMonth")}</span>
                    </div>
                  </StatCard>
                );
              })}
            </StatsGrid>

            <Card style={{ padding: 20, marginTop: 24 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{t("stores.quickActions")}</h2>
              {(storeLimitReached || trialExpired) && (
                <p style={{ margin: "12px 0 0", fontSize: 14, color: "#b45309", display: "flex", alignItems: "center", gap: 8 }}>
                  {trialExpired
                    ? t("billing.trialExpiredBanner")
                    : t("stores.storeLimitReached", { limit: storeLimit })}
                  {" · "}
                  <button
                    type="button"
                    onClick={() => navigate("/billing")}
                    style={{ background: "none", border: "none", padding: 0, fontWeight: 600, color: "#9b87f5", cursor: "pointer", textDecoration: "underline" }}
                  >
                    {t("billing.upgradeToContinue")}
                  </button>
                </p>
              )}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
                <Button onClick={() => setConnectOpen(true)} disabled={!canAddStore || trialExpired} title={!canAddStore ? t("stores.storeLimitReachedTitle") : undefined}>
                  <Plus size={16} />
                  {t("stores.connectNewStore")}
                </Button>
                <Button $variant="outline">
                  <Settings size={16} />
                  {t("stores.manageSettings")}
                </Button>
                <Button $variant="outline">
                  <Download size={16} />
                  {t("stores.exportData")}
                </Button>
              </div>
            </Card>

            <Card style={{ padding: 20 }}>
              <h3
                style={{
                  margin: 0,
                  marginBottom: 16,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {t("stores.storePerformance")}
              </h3>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="sales"
                      stroke="#2563eb"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      stroke="#16a34a"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="stores">
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ maxWidth: 420 }}>
                <div style={{ position: "relative" }}>
                  <Search
                    size={16}
                    color="#6b7280"
                    style={{
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                  <Input
                    placeholder={t("stores.searchStores")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ paddingLeft: 34 }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <Button $variant="outline" $size="sm">
                  <Filter size={14} />
                  {t("stores.moreFilters")}
                </Button>
                <Button $variant="outline" $size="sm">
                  <Download size={14} />
                  {t("stores.export")}
                </Button>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("stores.store")}</TableHead>
                    <TableHead>{t("stores.platform")}</TableHead>
                    <TableHead>{t("stores.location")}</TableHead>
                    <TableHead>{t("stores.status")}</TableHead>
                    <TableHead>{t("stores.ordersToday")}</TableHead>
                    <TableHead>{t("stores.revenue")}</TableHead>
                    <TableHead>{t("stores.lastSync")}</TableHead>
                    <TableHead style={{ textAlign: "right" }}>{t("stores.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(loading ? [] : filteredStores).map((store) => {
                    const c = getStatusColors(store.status);
                    const revenue = typeof store.revenue === "number" ? `$${store.revenue.toLocaleString()}` : store.revenue || "—";
                    const lastSync = store.lastSync ? new Date(store.lastSync).toLocaleString() : "—";
                    return (
                      <TableRow key={store._id}>
                        <TableCell>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              style={{
                                height: 32,
                                width: 32,
                                borderRadius: 9999,
                                background: "rgba(79,70,229,0.08)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Store size={16} color="#4f46e5" />
                            </div>
                            <div>
                              <div style={{ fontWeight: 600 }}>{store.name}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>{store.url}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span style={{ fontSize: 12, fontWeight: 600, textTransform: "capitalize" }}>
                            {store.platform || "woocommerce"}
                          </span>
                        </TableCell>
                        <TableCell>{store.location || "—"}</TableCell>
                        <TableCell>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              borderRadius: 9999,
                              padding: "4px 10px",
                              background: c.bg,
                              color: c.fg,
                              textTransform: "capitalize",
                            }}
                          >
                            {store.status}
                          </span>
                        </TableCell>
                        <TableCell>{store.ordersToday ?? "—"}</TableCell>
                        <TableCell>{revenue}</TableCell>
                        <TableCell>{lastSync}</TableCell>
                        <TableCell style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                            {syncingStoreId === store._id ? (
                              <span style={{ fontSize: 12, color: "#6b7280" }}>{t("stores.syncing")}</span>
                            ) : (
                              <>
                                <Button $variant="outline" $size="sm" onClick={() => handleSync(store, "all")} title={t("stores.syncAll")}>
                                  <RefreshCw size={14} /> {t("stores.sync")}
                                </Button>
                                <Button $variant="ghost" $size="icon" onClick={() => setMenuStore(store)} aria-label={t("stores.actions")}>
                                  <MoreVertical size={16} />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <div style={{ marginTop: 24 }}>
            <Card style={{ padding: 20 }}>
              <h3
                style={{
                  margin: 0,
                  marginBottom: 16,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {t("stores.storePerformance")}
              </h3>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="sales"
                      stroke="#2563eb"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      stroke="#16a34a"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="groups">
          <div style={{ marginTop: 24 }}>
            <Card style={{ padding: 20 }}>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 16,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {t("stores.storeGroups")}
              </h2>
              <div style={{ marginBottom: 16 }}>
                <Button onClick={() => setGroupModalOpen(true)}>
                  <Plus size={16} />
                  {t("stores.createNewGroup")}
                </Button>
              </div>
              <GroupsGrid>
                {groups.length === 0 ? (
                  <p style={{ color: "#6b7280", fontSize: 14 }}>{t("stores.noStoreGroups")}</p>
                ) : (
                  groups.map((g) => (
                    <GroupCard key={g._id}>
                      <h3 style={{ margin: 0, fontWeight: 600 }}>{g.name}</h3>
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                        {t("stores.storesCount", { count: Array.isArray(g.storeIds) ? g.storeIds.length : 0 })}
                      </p>
                    </GroupCard>
                  ))
                )}
              </GroupsGrid>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {connectOpen && (
        <ModalOverlay onClick={() => !loading && setConnectOpen(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t("stores.connectNewStoreTitle")}</ModalTitle>
            <ModalSub>{t("stores.connectNewStoreSub")}</ModalSub>
            <form onSubmit={handleConnectSubmit}>
              {connectError && <p style={{ color: "#dc2626", fontSize: 14, marginBottom: 16 }}>{connectError}</p>}
              <FormRow>
                <FormLabel>{t("stores.storeName")}</FormLabel>
                <Input
                  placeholder={t("getStarted.storeNamePlaceholder")}
                  value={connectName}
                  onChange={(e) => setConnectName(e.target.value)}
                  required
                />
              </FormRow>
              <FormRow>
                <FormLabel>{t("stores.platform")}</FormLabel>
                <select
                  value={connectPlatform}
                  onChange={(e) => setConnectPlatform(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 14,
                  }}
                >
                  <option value="woocommerce">{t("getStarted.woocommerce")}</option>
                  <option value="shopify">{t("getStarted.shopify")}</option>
                </select>
              </FormRow>
              <FormRow>
                <FormLabel>{t("stores.storeUrl")}</FormLabel>
                <Input
                  placeholder={connectPlatform === "shopify" ? t("stores.storeUrlHintShopify") : "https://example.com"}
                  value={connectUrl}
                  onChange={(e) => setConnectUrl(e.target.value)}
                  required
                />
                <FormHint>
                  {connectPlatform === "shopify" ? t("stores.storeUrlHintShopify") : t("stores.storeUrlHintWoo")}
                </FormHint>
              </FormRow>
              {connectPlatform === "woocommerce" && (
                <>
                  <FormRow>
                    <FormLabel>{t("stores.consumerKey")}</FormLabel>
                    <Input
                      type="text"
                      placeholder="ck_..."
                      value={connectConsumerKey}
                      onChange={(e) => setConnectConsumerKey(e.target.value)}
                      autoComplete="off"
                    />
                    <FormHint>{t("stores.consumerKeyHint")}</FormHint>
                  </FormRow>
                  <FormRow>
                    <FormLabel>{t("stores.consumerSecret")}</FormLabel>
                    <Input
                      type="password"
                      placeholder="cs_..."
                      value={connectConsumerSecret}
                      onChange={(e) => setConnectConsumerSecret(e.target.value)}
                      autoComplete="off"
                    />
                  </FormRow>
                </>
              )}
              {connectPlatform === "shopify" && (
                <FormRow>
                  <FormLabel>{t("stores.accessToken")}</FormLabel>
                  <Input
                    type="password"
                    placeholder="shpat_..."
                    value={connectAccessToken}
                    onChange={(e) => setConnectAccessToken(e.target.value)}
                    autoComplete="off"
                  />
                  <FormHint>{t("stores.accessTokenHint")}</FormHint>
                </FormRow>
              )}
              <ModalActions>
                <Button type="button" $variant="outline" onClick={() => setConnectOpen(false)} disabled={loading}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? t("stores.connecting") : t("stores.connectStore")}
                </Button>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

      {menuStore && (
        <ModalOverlay onClick={() => setMenuStore(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <ModalTitle>{t("stores.storeActions")}</ModalTitle>
            <ModalSub>{menuStore.name}</ModalSub>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button $variant="outline" onClick={() => handleImportProducts(menuStore)} style={{ justifyContent: "flex-start" }}>
                <Package size={16} style={{ marginRight: 8 }} />
                {t("stores.importProducts")}
              </Button>
              <Button $variant="outline" onClick={() => handleSync(menuStore, "orders")} style={{ justifyContent: "flex-start" }}>
                <RefreshCw size={16} style={{ marginRight: 8 }} />
                {t("stores.syncOrders")}
              </Button>
              <Button $variant="outline" onClick={() => handleSync(menuStore, "coupons")} style={{ justifyContent: "flex-start" }}>
                <Percent size={16} style={{ marginRight: 8 }} />
                {t("stores.syncCoupons")}
              </Button>
              <Button $variant="outline" onClick={() => handleSync(menuStore, "all")} style={{ justifyContent: "flex-start" }}>
                <RefreshCw size={16} style={{ marginRight: 8 }} />
                {t("stores.syncAll")}
              </Button>
              {menuStore.platform === "shopify" && (
                <Button $variant="outline" onClick={() => handleSync(menuStore, "inventory")} style={{ justifyContent: "flex-start" }}>
                  <RefreshCw size={16} style={{ marginRight: 8 }} />
                  {t("stores.syncInventory")}
                </Button>
              )}
              <hr style={{ margin: "8px 0", border: "none", borderTop: "1px solid #e5e7eb" }} />
              <Button $variant="outline" onClick={() => { setEditStore(menuStore); setMenuStore(null); }} style={{ justifyContent: "flex-start" }}>
                <Edit size={16} style={{ marginRight: 8 }} />
                {t("stores.editStore")}
              </Button>
              <Button $variant="outline" onClick={() => { setDeleteStoreId(menuStore._id); setMenuStore(null); }} style={{ justifyContent: "flex-start", color: "#dc2626", borderColor: "#fecaca" }}>
                <Trash2 size={16} style={{ marginRight: 8 }} />
                {t("stores.deleteStore")}
              </Button>
            </div>
            <div style={{ marginTop: 20 }}>
              <Button $variant="ghost" onClick={() => setMenuStore(null)}>{t("common.cancel")}</Button>
            </div>
          </ModalCard>
        </ModalOverlay>
      )}

      {editStore && (
        <ModalOverlay onClick={() => setEditStore(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t("stores.editStoreTitle")}</ModalTitle>
            <ModalSub>{t("stores.editStoreSub")}</ModalSub>
            <form onSubmit={handleEditStoreSubmit}>
              <FormRow>
                <FormLabel>{t("stores.storeName")}</FormLabel>
                <Input name="name" defaultValue={editStore.name} required />
              </FormRow>
              <FormRow>
                <FormLabel>{t("stores.storeUrl")}</FormLabel>
                <Input name="url" defaultValue={editStore.url} required />
              </FormRow>
              <FormRow>
                <FormLabel>{t("stores.status")}</FormLabel>
                <select name="status" defaultValue={editStore.status} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                  <option value="online">{t("stores.online")}</option>
                  <option value="offline">{t("stores.offline")}</option>
                  <option value="maintenance">{t("stores.maintenance")}</option>
                </select>
              </FormRow>
              <FormRow>
                <FormLabel>{t("stores.locationOptional")}</FormLabel>
                <Input name="location" defaultValue={editStore.location || ""} placeholder={t("stores.locationPlaceholder")} />
              </FormRow>
              <ModalActions>
                <Button type="button" $variant="outline" onClick={() => setEditStore(null)}>{t("common.cancel")}</Button>
                <Button type="submit">{t("common.save")}</Button>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}

      {deleteStoreId && (
        <ModalOverlay onClick={() => setDeleteStoreId(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <ModalTitle>{t("stores.deleteStoreTitle")}</ModalTitle>
            <ModalSub>{t("stores.deleteStoreSub")}</ModalSub>
            <ModalActions>
              <Button $variant="outline" onClick={() => setDeleteStoreId(null)}>{t("common.cancel")}</Button>
              <Button onClick={handleDeleteStoreConfirm} style={{ background: "#dc2626" }}>{t("stores.delete")}</Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}

      {groupModalOpen && (
        <ModalOverlay onClick={() => !groupSubmitting && setGroupModalOpen(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t("stores.createGroupTitle")}</ModalTitle>
            <ModalSub>{t("stores.createGroupSub")}</ModalSub>
            <form onSubmit={handleCreateGroupSubmit}>
              <FormRow>
                <FormLabel>{t("stores.groupName")}</FormLabel>
                <Input name="groupName" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder={t("stores.groupNamePlaceholder")} required />
              </FormRow>
              <FormRow>
                <FormLabel>{t("layout.stores")}</FormLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 200, overflowY: "auto" }}>
                  {stores.map((s) => (
                    <label key={s._id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={groupStoreIds.includes(s._id)}
                        onChange={(e) => setGroupStoreIds(e.target.checked ? [...groupStoreIds, s._id] : groupStoreIds.filter((id) => id !== s._id))}
                      />
                      <span>{s.name}</span>
                    </label>
                  ))}
                </div>
              </FormRow>
              <ModalActions>
                <Button type="button" $variant="outline" onClick={() => setGroupModalOpen(false)} disabled={groupSubmitting}>{t("common.cancel")}</Button>
                <Button type="submit" disabled={groupSubmitting}>{groupSubmitting ? t("stores.creating") : t("stores.createGroup")}</Button>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </div>
  );
}

